/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ActivateUserCollection } from "../models/ActivateUserCollection";
import type { CustomerUserInviteRequest } from "../models/CustomerUserInviteRequest";
import type { CustomerUserInviteResponse } from "../models/CustomerUserInviteResponse";
import type { User } from "../models/User";
import type { UserCreateRequest } from "../models/UserCreateRequest";
import type { UserOptions } from "../models/UserOptions";
import type { UserRolesUpdateRequest } from "../models/UserRolesUpdateRequest";
import type { UserRolesUpdateResponse } from "../models/UserRolesUpdateResponse";

import type { CancelablePromise } from "../core/CancelablePromise";
import { OpenAPI } from "../core/OpenAPI";
import { request as __request } from "../core/request";

export class UserService {
  /**
   * Remove a user from a customer
   * Remove a user from a customer
   * @param customerId
   * @param userId
   * @returns void
   * @throws ApiError
   */
  public static removeUserFromCustomer(
    customerId: string,
    userId: string
  ): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/customer/{customerId}/user/{userId}",
      path: {
        customerId: customerId,
        userId: userId,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * Activate a user's account.
   * Create and activate a user's account using their auth token
   * @returns ActivateUserCollection OK
   * @throws ApiError
   */
  public static activateUser(): CancelablePromise<ActivateUserCollection> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/user/activate",
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        409: `User is already active`,
        422: `ValidationErrorResponse`,
      },
    });
  }

  /**
   * Get my user details
   * Get user details for authenticated user
   * @returns User OK
   * @throws ApiError
   */
  public static getUserMe(): CancelablePromise<User> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/user/me",
      errors: {
        401: `Unauthorized`,
        404: `User doesn't exist for you`,
      },
    });
  }

  /**
   * Create a new user for myself
   * Create a user for the currently authenticated user
   * @param requestBody
   * @returns User OK
   * @throws ApiError
   */
  public static createUserMe(
    requestBody: UserCreateRequest
  ): CancelablePromise<User> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/user/me",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        409: `User already exists`,
        422: `ValidationErrorResponse`,
      },
    });
  }

  /**
   * Update my options
   * Updates options for currently authenticated user
   * @param requestBody
   * @returns UserOptions OK
   * @throws ApiError
   */
  public static updateUserMeOptions(
    requestBody: UserOptions
  ): CancelablePromise<UserOptions> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/user/me/options",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        401: `Unauthorized`,
        404: `User doesn't exist for you`,
        422: `ValidationErrorResponse`,
      },
    });
  }

  /**
   * Invite a user to a customer
   * Creates and sends an invite token to the prospective user, on signup the user will be added to the specified customer.
   * @param requestBody
   * @returns CustomerUserInviteResponse OK
   * @throws ApiError
   */
  public static inviteUserToCustomer(
    requestBody: CustomerUserInviteRequest
  ): CancelablePromise<CustomerUserInviteResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/user/invite",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        409: `User already invited`,
        422: `ValidationErrorResponse`,
      },
    });
  }

  /**
   * Validate and return a user's invitation to customer
   * Checks the access token and returns the invite information if valid
   * @returns CustomerUserInviteResponse OK
   * @throws ApiError
   */
  public static getCustomerUserInvite(): CancelablePromise<CustomerUserInviteResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/user/invite",
      errors: {
        400: `Missing 'token' from request`,
        401: `Unauthorized`,
        404: `Invite not found`,
        422: `ValidationErrorResponse`,
      },
    });
  }

  /**
   * Revoke a customer invite for a user
   * Revoke a customer invite for a user
   * @param email Invited email address
   * @param customerId Customer id for which to revoke invite
   * @returns CustomerUserInviteResponse OK
   * @throws ApiError
   */
  public static revokeInviteForUser(
    email: string,
    customerId: string
  ): CancelablePromise<CustomerUserInviteResponse> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/user/invite",
      query: {
        email: email,
        customer_id: customerId,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        404: `Invite not found`,
      },
    });
  }

  /**
   * Resend a customer invite for a user
   * Resend a customer invite for a user
   * @param email Invited email address
   * @param customerId Customer id for which to resend invite
   * @returns CustomerUserInviteResponse OK
   * @throws ApiError
   */
  public static resendInviteForUser(
    email: string,
    customerId: string
  ): CancelablePromise<CustomerUserInviteResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/user/invite/resend",
      query: {
        email: email,
        customer_id: customerId,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        404: `Invite not found`,
      },
    });
  }

  /**
   * Update roles for a user
   * Updates roles for a user based on the user id and request body replacing the existing roles for the user
   * @param userId
   * @param requestBody
   * @returns UserRolesUpdateResponse OK
   * @throws ApiError
   */
  public static updateRolesForUser(
    userId: string,
    requestBody: UserRolesUpdateRequest
  ): CancelablePromise<UserRolesUpdateResponse> {
    return __request(OpenAPI, {
      method: "PUT",
      url: "/user/{userId}/roles",
      path: {
        userId: userId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        422: `ValidationErrorResponse`,
      },
    });
  }
}
